import Cookies from "js-cookie";
import HeaderSearch from "./components/HeaderSearch.js";
import CMP from "./components/CMP.js";
import TonefuseAds from "./components/TonefuseAds.js";
import AdSenseAds from "./components/AdSenseAds.js";
import GoogleTagManager from "./components/GoogleTagManager.js";
import Clarity from "./components/Clarity.js";
import { withDevModeFailover, withNoAdsFailover } from "./helpers.js";
import AdBlockNotice from "./components/AdBlockNotice.js";
import HeaderLoginToggle from "./components/HeaderLoginToggle.js";
import MegaMenu from "./components/MegaMenu.js";
import ParallaxSeats from "./components/ParallaxSeatsDom.js";
import {
  addAuthEventListener,
  getCurrentUser,
  handleVerifyByHash,
  loginWithAuthToken,
  openLoginDialog,
  openProfileDialog,
} from "./auth/Auth.js";
import Notifications from "./components/Notifications.js";
import { determineWatchlistButtonState } from "./components/WatchlistToggle.js";

if (process.env.NODE_ENV === "development") {
  console.log("Debug logging is enabled");
}

const preloadGoogleFonts = document.getElementById("__preload_googlefonts");
if (preloadGoogleFonts.rel != "stylesheet") preloadGoogleFonts.rel = "stylesheet";

const AUTH_COOKIE_NAME = "auth2";

/* COMMON EVENT LISTENERS */

let commonEventListeners = [];
let Common = {
  addEventListener: (event, listener) => {
    event.split(" ").forEach((event) => {
      commonEventListeners.push({
        event: event,
        listener: listener,
      });
    });
  },
  triggerEvent: (groupEvent, payload) => {
    if (process.env.NODE_ENV === "development") console.log("[Event handler] Triggered:", groupEvent, payload);
    let [group, event] = groupEvent.split(":");
    commonEventListeners.forEach((eventListener) => {
      if (eventListener.event == group + ":all" || eventListener.event == group + ":" + event) {
        eventListener.listener(event, payload);
      }
    });
  },
};

let headerSearch;
let headerLoginToggleComponent;
let notifications = null;

const handleSearchToggleClick = (event) => {
  if (headerSearch) headerSearch.open(event);
};

const handleLoginToggleClick = async () => {
  const currentUser = await getCurrentUser();
  if (currentUser) {
    openProfileDialog();
  } else {
    openLoginDialog();
  }
};

export const showNotification = (title, message, autohideTimeout, icon) => {
  if (!notifications) {
    notifications = Notifications(document.querySelector("#notifications")).mount();
  }
  notifications.add(title, message, icon).show(autohideTimeout);
};

const loadCrispScript = (consentPromise) => {
  return new Promise((resolve) => {
    consentPromise.then(({ analytics }) => {
      if (!analytics) {
        console.log("Crisp chat will not be loaded due to cookie consent");
        resolve(null);
        return;
      }

      window.$crisp = [];
      const scriptElement = document.createElement("script");
      scriptElement.src = "https://client.crisp.chat/l.js";
      scriptElement.async = 1;
      scriptElement.onload = () => {
        resolve(window.$crisp);
      };
      scriptElement.onerror = () => {
        console.error("Failed to load Crisp chat script");
        resolve(null); // Resolve anyway to avoid blocking other functionality
      };
      document.getElementsByTagName("head")[0].appendChild(scriptElement);
    });
  });
};

let windowLoaded = false;

Common.initPage = (onLoad) => {
  const loadPage = async () => {
    const consentPromise = withNoAdsFailover(
      withDevModeFailover(
        new Promise((resolve) => {
          CMP({
            onConsent: resolve,
          }).render();
        }),
        { analytics: false, ads: false } // Development mode defaults (change to true for testing)
      ),
      { analytics: false, ads: false }
    );

    AdBlockNotice(consentPromise, {
      onDetected: () => {
        showNotification(
          "Reklámblokkoló észlelve",
          "Az oldal ingyenes használatát reklámok teszik lehetővé. Kérjük, támogasd a munkánkat a blokkoló kikapcsolásával!",
          false,
          "fas fa-hand-holding-heart"
        );
      },
    }).render();

    AdSenseAds({
      preloadScriptId: "__adsense_script_preload",
    }).render();

    GoogleTagManager({
      gtmId: window.google_tag_id,
      consentPromise: consentPromise,
    }).render();

    Clarity({
      projectId: window.clarity_project_id,
      consentPromise: consentPromise,
    }).render();

    TonefuseAds({
      preloadScriptId: "__tonefuse_script_preload",
      consentPromise: consentPromise,
    }).render();

    if (window.__sentryEnableAfterConsent) {
      window.__sentryEnableAfterConsent(withDevModeFailover(consentPromise, { analytics: true }));
    }

    addAuthEventListener("all", (event, payload) => {
      Common.triggerEvent("auth:" + event, payload);
    });

    // add common login event listeners
    Common.addEventListener("auth:successful_login", (_event, payload) => {
      Cookies.set(AUTH_COOKIE_NAME, payload.user.token, { expires: 10, path: "/" });
      if (headerLoginToggleComponent) headerLoginToggleComponent.el.classList.add("logged-in-successfully");
      if (headerLoginToggleComponent) headerLoginToggleComponent.setLoading(false);
      if (payload.showNotification)
        showNotification(
          "Sikeres bejelentkezés",
          "Üdv a Mozipremiereken, " + payload.user.name + "!",
          null,
          "fas fa-hand-wave"
        );
    });

    Common.addEventListener("auth:successful_logout", (_event, payload) => {
      if (!payload.dontRemoveCookie) Cookies.remove(AUTH_COOKIE_NAME);
      if (headerLoginToggleComponent) headerLoginToggleComponent.el.classList.remove("logged-in-successfully");
      if (headerLoginToggleComponent) headerLoginToggleComponent.setLoading(false);
      if (payload.showNotification)
        showNotification("Sikeres kijelentkezés", "Várunk vissza!", null, "fas fa-hand-wave");
    });

    Common.addEventListener("auth:watchlist_movie_added", () => {
      showNotification("Hozzáadva", "A film hozzáadásra került a kedvenceidhez.", null, "fas fa-heart");
    });

    Common.addEventListener("auth:watchlist_movie_removed", () => {
      showNotification("Eltávolítva", "A film eltávolításra került a kedvenceid közül.", null, "fas fa-heart-crack");
    });

    Common.addEventListener("auth:login_by_hash auth:login_by_cookie", () => {
      document.querySelectorAll(".add-to-watchlist-button").forEach((button) => {
        button.disabled = true;
      });
    });

    Common.addEventListener("auth:user_data_changed", (_event, payload) => {
      document.querySelectorAll(".add-to-watchlist-button").forEach((button) => {
        button.disabled = false;
        determineWatchlistButtonState(button, payload ? payload.user : null);
      });
    });

    if (onLoad) await onLoad(consentPromise);

    ParallaxSeats().mount();

    const hoverAdCheckbox = document.querySelector(".hide-hover-ad-checkbox");
    if (hoverAdCheckbox) {
      hoverAdCheckbox.addEventListener("change", (event) => {
        const crispClient = document.querySelector(".crisp-client");
        console.log("Hide hover ad changed:", event.target.checked, crispClient);
        if (event.target.checked) {
          if (crispClient) crispClient.classList.add("hover-ad-closed");
        } else {
          if (crispClient) crispClient.classList.remove("hover-ad-closed");
        }
      });
    }

    headerSearch = HeaderSearch(document.querySelector("#header-search"), {
      preloadScriptId: "__lunr_script_preload",
      minimumQueryLength: 2,
    }).mount();

    // Setup toggle handlers
    document.querySelectorAll("label[for='header-search-field']").forEach((toggle) => {
      toggle.addEventListener("click", handleSearchToggleClick, false);
    });

    headerLoginToggleComponent = HeaderLoginToggle(document.querySelector("#header-login-toggle"), {
      onLoginToggleClick: () => handleLoginToggleClick(),
    }).mount();

    // Initialize Mega Menu
    const headerElement = document.querySelector("header#header");
    if (headerElement) {
      MegaMenu(headerElement).mount();
    }

    headerLoginToggleComponent.setLoading(true);

    const verifyHash = document.location.hash.match(/login-([a-z0-9]+)$/);
    if (verifyHash) {
      Common.triggerEvent("auth:login_by_hash");
      await handleVerifyByHash(verifyHash[1]);
    }

    const currentUser = await getCurrentUser();
    if (!currentUser) {
      const authToken = Cookies.get(AUTH_COOKIE_NAME);
      if (authToken) {
        // loginWithAuthToken will trigger auth:login_by_cookie event itself
        await loginWithAuthToken(authToken);
      }
    }

    headerLoginToggleComponent.setLoading(false);

    if (window.CRISP_WEBSITE_ID) {
      await loadCrispScript(withDevModeFailover(consentPromise, { analytics: true }));
    }
  };

  if (windowLoaded) {
    loadPage();
  } else {
    window.addEventListener(
      "load",
      () => {
        windowLoaded = true;
        loadPage();
      },
      false
    );
  }
};

Common.resetPage = (onReset) => {
  if (headerSearch && headerSearch.mounted) {
    headerSearch.unmount();
  }

  document.querySelectorAll("label[for='header-search-field']").forEach((toggle) => {
    toggle.removeEventListener("click", handleSearchToggleClick, false);
  });

  if (onReset) onReset();
};

export default Common;
